import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import AppContainer from "../../Components/Accounting/AppContainer";
import ExportBar from "../../Components/Accounting/ExportBar";
import { RenderSecure } from "../../util/script/RenderSecure";
import { ajaxPayroll } from "../../util/accounting/remote/ajaxAccounting";
import functions from "../../util/accounting/functions";
import toast, { Toaster } from "react-hot-toast";

const PayrollRunDetail = () => {
  const { id }   = useParams();
  const navigate = useNavigate();
  const [run,     setRun]     = useState(null);
  const [payslips,setPayslips]= useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (id) fetchRun(); }, [id]);

  const fetchRun = async () => {
    setLoading(true);
    const [runRes, slipRes] = await Promise.all([
      ajaxPayroll.getPayrollRun({ run_id: parseInt(id) }),
      ajaxPayroll.getPayslips({ run_id: parseInt(id) }),
    ]);
    if (runRes.status === "OK") setRun(runRes.details);
    else { toast.error("Run not found"); navigate("/accounting/payroll"); }
    if (slipRes.status === "OK") setPayslips(Array.isArray(slipRes.details) ? slipRes.details : slipRes.details?.list || []);
    setLoading(false);
  };

  const handleApprove = async () => {
    const res = await ajaxPayroll.approvePayroll({ run_id: parseInt(id) });
    if (res.status === "OK") { toast.success("Payroll approved and posted to ledger!"); fetchRun(); }
    else toast.error(res.message);
  };

  if (loading) return <AppContainer title="Payroll Run" previousPageTitle="Payroll"><div className="text-center py-5"><i className="fa-solid fa-spinner fa-spin fa-2x tx-primary"></i></div></AppContainer>;
  if (!run) return null;

  const totalGross   = payslips.reduce((s,p)=>s+(parseFloat(p.gross_pay)||0),0);
  const totalPAYE    = payslips.reduce((s,p)=>s+(parseFloat(p.paye)||0),0);
  const totalNSSFee  = payslips.reduce((s,p)=>s+(parseFloat(p.nssf_employee)||0),0);
  const totalNSSFer  = payslips.reduce((s,p)=>s+(parseFloat(p.nssf_employer)||0),0);
  const totalLST     = payslips.reduce((s,p)=>s+(parseFloat(p.lst)||0),0);
  const totalDeduc   = payslips.reduce((s,p)=>s+(parseFloat(p.total_other_deductions)||0),0);
  const totalNet     = payslips.reduce((s,p)=>s+(parseFloat(p.net_pay)||0),0);

  const exportCols = [
    {key:"employee_number",label:"Emp #"},{key:"full_name",label:"Name"},
    {key:"basic_salary",label:"Basic"},{key:"gross_pay",label:"Gross"},
    {key:"paye",label:"PAYE"},{key:"nssf_employee",label:"NSSF(Ee)"},{key:"nssf_employer",label:"NSSF(Er)"},
    {key:"lst",label:"LST"},{key:"total_other_deductions",label:"Deductions"},{key:"net_pay",label:"Net Pay"},
  ];

  return (
    <AppContainer
      title={`Payroll Run — ${run.pay_period}`}
      previousPageTitle="Payroll"
      actions={[
        ...(run.status === "draft" ? [{
          label:"Approve & Post to Ledger", icon:"fa-solid fa-check", className:"btn btn-success", onClick:handleApprove,
        }] : []),
        { label:"Print",        icon:"fa-solid fa-print",      className:"btn btn-outline-secondary", onClick:()=>window.print() },
        { label:"All Runs",     icon:"fa-solid fa-arrow-left", className:"btn btn-light",             onClick:()=>navigate("/accounting/payroll") },
      ]}
    >
      <Toaster />

      {/* Summary KPIs */}
      <div className="row g-2 mb-3">
        {[
          {label:"Period",        value:run.pay_period, type:"text", color:"#6366f1"},
          {label:"Employees",     value:payslips.length, type:"count", color:"#0ea5e9"},
          {label:"Total Gross",   value:totalGross,     color:"#10b981"},
          {label:"Total PAYE",    value:totalPAYE,      color:"#ef4444"},
          {label:"NSSF (Ee+Er)", value:totalNSSFee+totalNSSFer, color:"#f59e0b"},
          {label:"Total Net Pay", value:totalNet,       color:"#10b981"},
          {label:"Status",        value:run.status,     type:"badge", color: run.status==="approved"?"#10b981":"#f59e0b"},
        ].map((c,i)=>(
          <div key={i} className="col-xl col-sm-4 col-6">
            <div className="card p-2" style={{borderLeft:`3px solid ${c.color}`}}>
              <div className="tx-11 tx-muted">{c.label}</div>
              <div className="fw-bold" style={{color:c.color, fontSize:"0.9rem"}}>
                {c.type==="text" ? c.value :
                 c.type==="count" ? c.value :
                 c.type==="badge" ? <span className="badge" style={{background:c.color}}>{c.value}</span> :
                 functions.formatCurrency(c.value)}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Payslip table */}
      <div className="card custom-card p-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="fw-semibold mb-0">Payslips ({payslips.length} employees)</h6>
          <ExportBar data={payslips} columns={exportCols}
            filename={`payroll-${run.pay_period||id}`} pdfTitle={`Payroll Run — ${run.pay_period||id}`}
            printCode="ACC-PAYROLL" exportCode="ACC-PAYROLL" />
        </div>
        <div className="table-responsive">
          <table className="table table-bordered table-sm" style={{fontSize:"0.82rem"}}>
            <thead className="bg-light">
              <tr>
                <th>Emp #</th><th>Name</th><th>Basic</th><th>Allowances</th><th>Gross</th>
                <th className="tx-danger">PAYE</th><th className="tx-danger">NSSF(Ee)</th><th className="tx-danger">NSSF(Er)</th>
                <th className="tx-danger">LST</th><th className="tx-danger">Other Ded.</th>
                <th className="tx-success">Net Pay</th>
              </tr>
            </thead>
            <tbody>
              {payslips.map((p,i)=>(
                <tr key={i}>
                  <td><code>{p.employee_number}</code></td>
                  <td><Link to={`/accounting/payroll/employee/${p.employee_id}`} className="fw-semibold">{p.full_name}</Link></td>
                  <td>{functions.formatCurrency(p.basic_salary)}</td>
                  <td className="tx-success">{functions.formatCurrency(p.total_allowances)}</td>
                  <td>{functions.formatCurrency(p.gross_pay)}</td>
                  <td className="tx-danger">{functions.formatCurrency(p.paye)}</td>
                  <td className="tx-danger">{functions.formatCurrency(p.nssf_employee)}</td>
                  <td className="tx-danger">{functions.formatCurrency(p.nssf_employer)}</td>
                  <td className="tx-danger">{functions.formatCurrency(p.lst)}</td>
                  <td className="tx-danger">{functions.formatCurrency(p.total_other_deductions)}</td>
                  <td className="tx-success fw-bold">{functions.formatCurrency(p.net_pay)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-light fw-semibold">
              <tr>
                <td colSpan={2}>Totals</td>
                <td colSpan={2}></td>
                <td>{functions.formatCurrency(totalGross)}</td>
                <td className="tx-danger">{functions.formatCurrency(totalPAYE)}</td>
                <td className="tx-danger">{functions.formatCurrency(totalNSSFee)}</td>
                <td className="tx-danger">{functions.formatCurrency(totalNSSFer)}</td>
                <td className="tx-danger">{functions.formatCurrency(totalLST)}</td>
                <td className="tx-danger">{functions.formatCurrency(totalDeduc)}</td>
                <td className="tx-success">{functions.formatCurrency(totalNet)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
        {run.notes && <div className="alert alert-light tx-12 mt-2"><strong>Notes:</strong> {run.notes}</div>}
      </div>
    </AppContainer>
  );
};

export default PayrollRunDetail;
